import type { ReactNode } from "react";
import { cn } from "../../lib/utils";
import { getPayloadConfigFromPayload, useChartContext } from "./context";
import { ChartTooltipRowContext, useChartTooltipContext } from "./ChartTooltipContext";
import { ChartTooltipIndicator } from "./ChartTooltipIndicator";
import { ChartTooltipName } from "./ChartTooltipName";
import { ChartTooltipValue } from "./ChartTooltipValue";

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

export interface ChartTooltipRowsProps {
  /** Per-row composition; omit for the default indicator + name + value row. */
  children?: ReactNode;
  className?: string;
}

/** One row per hovered series, each providing row context to the tooltip row parts. */
export const ChartTooltipRows = ({ children, className }: ChartTooltipRowsProps) => {
  const { config } = useChartContext();
  const { payload } = useChartTooltipContext();

  return (
    <div data-slot="chart-tooltip-rows" className={cn("grid gap-1.5", className)}>
      {payload.map((item, index) => {
        const key = `${item.name ?? item.dataKey ?? "value"}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);
        const fill = isRecord(item.payload) && typeof item.payload.fill === "string" ? item.payload.fill : undefined;
        return (
          <ChartTooltipRowContext.Provider
            key={String(item.dataKey ?? item.name ?? index)}
            value={{
              item,
              name: itemConfig?.label ?? item.name,
              value: item.value,
              indicatorColor: fill ?? item.color,
              icon: itemConfig?.icon,
            }}
          >
            <div
              data-slot="chart-tooltip-row"
              className="[&>svg]:text-muted-foreground flex w-full flex-wrap items-center gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5"
            >
              {children ?? (
                <>
                  <ChartTooltipIndicator />
                  <ChartTooltipName />
                  <ChartTooltipValue />
                </>
              )}
            </div>
          </ChartTooltipRowContext.Provider>
        );
      })}
    </div>
  );
};
